export const GOAL_TAGS = [
  "Academic", "Fitness", "Startup", "Programming", "Writing", "Language Learning",
  "Music", "Exam Prep", "Research", "Meditation", "Reading", "Career Switch",
  "Job Hunting", "Business", "Art", "Finance", "Habits", "Sports",
  "Content Creation", "Public Speaking",
];

export const CHRONOTYPES = [
  { key: "morning", label: "Morning person", hint: "Sunrise operator. Coffee, then war." },
  { key: "night", label: "Night owl", hint: "The world sleeps. You compound." },
  { key: "flexible", label: "Flexible", hint: "You bend the day to your will." },
];

export const FOCUS_LENGTHS = ["25 mins", "45 mins", "60 mins", "90 mins", "2 hours", "Flexible"];

export const PRODUCTIVITY_METHODS = [
  "Pomodoro", "Deep Work", "Time Blocking", "Task Lists", "Sprint Sessions", "No Preference",
];

export const CHALLENGES = [
  "Procrastination", "Phone Addiction", "Inconsistency", "Perfectionism",
  "Burnout", "Motivation", "Planning", "Distractions",
];

export const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export const MEETING_FREQ = ["Daily", "Every 2 Days", "Weekly", "Flexible"];
export const SESSION_DURATIONS = ["30 mins", "45 mins", "1 hr", "2 hrs", "Flexible"];

export const COMMUNICATION = [
  "Voice", "Video", "Text", "Screen Share", "Discord", "Zoom", "Google Meet", "Telegram", "WhatsApp",
];

export const ACCOUNTABILITY_STYLES = [
  "Daily check-ins", "Morning reminders", "Night reviews", "Strict deadlines",
  "Gentle encouragement", "Direct criticism", "Motivational messages",
  "Random nudges", "Scheduled calls", "Silent co-working", "Celebrating wins",
];

export const DISAPPEAR_ACTIONS = [
  { key: "spam", label: "Spam message me", hint: "Notification carpet-bomb until I reply." },
  { key: "call", label: "Call me", hint: "Ring me until I pick up." },
  { key: "roast", label: "Roast me", hint: "No mercy. Bring the receipts." },
  { key: "remind", label: "Remind politely", hint: "One nudge. That’s enough." },
  { key: "space", label: "Give me space", hint: "Something serious might be up." },
];

export const MOTIVATION_TYPES = [
  { key: "letdown", label: "Fear of letting someone down" },
  { key: "competition", label: "Friendly competition" },
  { key: "positive", label: "Positive reinforcement" },
  { key: "streaks", label: "Tracking streaks" },
  { key: "deadlines", label: "External deadlines" },
];

export const LOOKING_FOR = [
  "Same Goal", "Same Skill", "Same Industry", "Just Someone Consistent", "No Preference",
];

export const PARTNER_LEVEL = ["Same", "Higher", "Lower", "No Preference"];
export const GENDERS = ["Male", "Female", "Non-binary", "Prefer not to say", "Custom"];

export const DEAL_BREAKERS = [
  "Frequently cancels", "Late replies", "Inactive", "Too casual",
  "Negative attitude", "Misses check-ins", "Doesn't communicate",
];

export const INTERESTS = [
  "Gaming", "Anime", "Books", "AI", "Music", "Finance", "Sports", "Coding",
  "Travel", "Movies", "Photography", "Cooking", "Design", "Philosophy",
  "History", "Chess", "Podcasts", "Cinema",
];

export const COMMITMENT_DURATIONS = ["2 weeks", "1 month", "3 months", "6 months", "1 year", "Until Goal Completed"];

export const ACTIVE_PARTNERSHIPS = ["1", "2", "3", "Decide later"];

export const PARTNERSHIP_INTENTS = [
  { key: "accountability_only", label: "Strictly accountability", hint: "Goals, check-ins, and progress only. Keep the connection focused and structured." },
  { key: "accountability_friendship", label: "Accountability, open to friendship", hint: "Accountability comes first; a genuine friendship can grow naturally over time." },
  { key: "friendship_growth", label: "Growth-focused friendship", hint: "Build a supportive friendship while actively helping each other stay consistent." },
  { key: "professional_networking", label: "Professional connection", hint: "Accountability with room for networking, collaboration, or industry exchange." },
  { key: "open_to_romance", label: "Open to a relationship", hint: "Accountability is the starting point; open to romance only if interest is mutual." },
  { key: "flexible", label: "Let the connection evolve", hint: "No fixed expectation beyond respectful accountability and clear boundaries." },
];

export function partnershipIntentLabel(value) {
  return PARTNERSHIP_INTENTS.find((option) => option.key === value)?.label || "";
}

// Profile icons — 12 curated lucide icons
export const PROFILE_ICONS = [
  "Rocket", "Brain", "Flame", "Target", "Trophy", "BookOpen",
  "Dumbbell", "Code2", "Feather", "Sparkles", "Compass", "Zap",
];

export const USAGE_MODES = [
  { key: "solo", label: "Solo", title: "Fly solo", description: "Track your productivity and stay accountable without needing a partner." },
  { key: "partner", label: "Partner", title: "Partner based", description: "Search and match with accountability partners and work together." },
  { key: "community", label: "Group / Community", title: "Group / Community", description: "Work alongside multiple people in a shared community." },
];

export const SOLO_EXCLUDED_SECTIONS = ["accountability", "partner", "commitment", "intent"];

export const SECTIONS = [
  { key: "welcome", title: "Welcome", overline: "00" },
  { key: "basic", title: "Basic Information", overline: "01" },
  { key: "goals", title: "Your Goals", overline: "02" },
  { key: "work", title: "Your Work Style", overline: "03" },
  { key: "schedule", title: "Your Schedule", overline: "04" },
  { key: "accountability", title: "Accountability Style", overline: "05" },
  { key: "partner", title: "Partner Preferences", overline: "06" },
  { key: "personality", title: "Personality", overline: "07" },
  { key: "commitment", title: "Commitment", overline: "08" },
  { key: "intent", title: "Connection Intent", overline: "09" },
  { key: "review", title: "Review & Submit", overline: "10" },
];

export function sectionsForMode(mode) {
  if (mode === "solo") return SECTIONS.filter((section) => !SOLO_EXCLUDED_SECTIONS.includes(section.key));
  return SECTIONS;
}

export const emptyProfile = () => ({
  // mode
  usageMode: "",
  // basic
  fullName: "", username: "", age: "", gender: "", customGender: "",
  profileIcon: "Rocket", bio: "", city: "", country: "", timezone: "",
  // goals
  goals: [{ id: Date.now(), tags: [], customGoal: "", description: "", target: "", targetDate: "", importance: 7, isPrimary: true }],
  goalTags: [], customGoal: "", goalDescription: "", level: "",
  target: "", targetDate: "", importance: 7, isPrimary: true,
  // work style
  chronotype: "", dailyHours: 4, focusLength: "", productivityMethods: [],
  challenges: [], customChallenge: "", missGoalsStory: "",
  idealDay: [], nonNegotiableBlocks: [],
  // schedule
  workingDays: [], availableStart: "07:00", availableEnd: "22:00",
  meetingFrequency: "", sessionDuration: "", communication: [], comfortableTimezones: [],
  // accountability
  accountabilityStyle: [], disappearAction: "", motivationType: "",
  strictness: 5, honesty: 7, shareProgress: false, sharedGoals: false,
  // partner
  lookingFor: "", partnerLevel: "", ageRange: [22, 40], partnerGender: "No Preference",
  languagesSpoken: [], languagesPreferred: [], minCommitment: 2, maxTimezoneDiff: 6,
  dealBreakers: [], partnerDescription: "",
  // personality
  introExtro: 5, plannerSpont: 5, compCoop: 5, seriousFun: 5, optRealist: 5, logicEmo: 5,
  interests: [], quote: "", funFact: "",
  // commitment
  commitmentDuration: "", activePartnerships: "",
  // connection intent
  partnershipIntent: "",
  // meta
  completedAt: null,
});
